// stores/googleMaps.js
import { defineStore } from 'pinia'
import {
  getCityFromSuggestion,
  getCountryFromSuggestion,
  getPostalCodeFromSuggestion,
  GoogleMapsService,
} from '../../utils/google-maps.service'
import { ref } from 'vue'
import { FormattedAddressItem } from '@/components/forms/addressForm/addressUtils'

export const useGoogleMapsStore = defineStore('googleMaps', () => {
  const googleMapsService = ref()
  const RefPlace = ref()

  function initialize(apiKey) {
    googleMapsService.value = new GoogleMapsService(apiKey)
  }

  async function getFormatedAddressFromPlace(placePredictionId) {
    // Use place ID to create a new Place instance.
    const Place = RefPlace.value
    const place = new Place({
      id: placePredictionId,
      requestedLanguage: 'fr', // optional
    })

    await place.fetchFields({
      fields: [
        'displayName',
        'formattedAddress',
        'location',
        'addressComponents',
      ],
    })

    const city = getCityFromSuggestion(place)
    const postalCode = getPostalCodeFromSuggestion(place)
    const country = getCountryFromSuggestion(place)

    const addressItem: FormattedAddressItem = {
      addressLine: place.displayName,
      latitude: String(place.location?.lat()),
      longitude: String(place.location?.lng()),
      country,
      postalCode,
      city,
      formattedAddress: place.formattedAddress,
    }

    return addressItem
  }

  /**
   *
   * @param text
   * @param regions
   * @returns Promise<{ label: string; value: any }[]> with value: the suggestion.placePrediction object
   */
  async function getAutocompleteSuggestionsPlaces(
    text: string,
    regions: string[],
  ): Promise<{ label: string; value: any }[]> {
    const { AutocompleteSessionToken, AutocompleteSuggestion, Place } =
      await googleMapsService.value.initPlacesLibrary()

    RefPlace.value = Place

    const request = {
      input: text,
      language: 'fr-FR',
      region: 'fr',
      includedRegionCodes: regions,
    }

    const token = new AutocompleteSessionToken()
    // @ts-ignore
    request.sessionToken = token

    const { suggestions } =
      await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

    const placesSuggested = []
    for (const suggestion of suggestions) {
      const placePrediction = suggestion.placePrediction

      placesSuggested.push({
        label: placePrediction.text.toString(),
        value: placePrediction.toPlace().id,
      })
    }

    return placesSuggested
  }

  const getReverseGoogleGeoCoding = async (
    lat: string | number,
    lng: string | number,
  ) => {
    const geocoder = await googleMapsService.value.initGeocoderLibrary()

    const response = geocoder.geocode({ location: { lat, lng } })

    return response
  }

  const getFormatedAddressFromQuery = async ({
    searchQuery,
    city,
    postalCode,
    country,
  }: {
    searchQuery: string
    city?: string | null
    postalCode?: string | null
    country?: string | null
  }): Promise<FormattedAddressItem | null> => {
    const geocoder = await googleMapsService.value.initGeocoderLibrary()

    const addressParts = [searchQuery, postalCode, city, country].filter(
      Boolean,
    )
    const fullAddress = addressParts.join(', ')

    try {
      const { results } = await geocoder.geocode({
        address: fullAddress,
        language: 'fr',
      })

      const result = results[0]
      const location = result.geometry.location

      const getComponent = (type: string) =>
        result.address_components.find(comp => comp.types.includes(type))
          ?.long_name || ''

      const addressItem: FormattedAddressItem = {
        addressLine: result.formatted_address,
        latitude: String(location.lat()),
        longitude: String(location.lng()),
        city:
          getComponent('locality') ||
          getComponent('administrative_area_level_2'),
        postalCode: getComponent('postal_code'),
        country: getComponent('country'),
        formattedAddress: result.formatted_address,
      }
      return addressItem
    } catch (error) {
      return null
    }
  }

  return {
    googleMapsService,
    initialize,
    getFormatedAddressFromPlace,
    getAutocompleteSuggestionsPlaces,
    getReverseGoogleGeoCoding,
    getFormatedAddressFromQuery,
  }
})
